/**
 * Debug Logging System
 *
 * Centralized debug logging with localStorage-based configuration.
 * Only admins can enable/disable logging levels via Admin Dashboard.
 *
 * Usage:
 * ```typescript
 * import { debug } from '@/lib/debug';
 *
 * debug.log('Info message');
 * debug.warn('Warning message');
 * debug.error('Error message');
 * ```
 *
 * Configuration (Admin only):
 * - Stored in localStorage: 'debug_settings'
 * - Default: All logging disabled
 * - Levels: log, warn, error
 */

const STORAGE_KEY = "debug_settings";
const DEBUG_PANEL_VISIBILITY_KEY = "storytify_debug_panel_visible";

export interface DebugSettings {
  enabled: boolean;
  levels: {
    log: boolean;
    warn: boolean;
    error: boolean;
  };
}

/**
 * Debug Panel Visibility Functions
 * Controls whether the debug panel is shown in the UI (development only)
 */
export function isDebugPanelVisible(): boolean {
  if (!import.meta.env.DEV) return false;
  const stored = localStorage.getItem(DEBUG_PANEL_VISIBILITY_KEY);
  return stored === "true"; // Default: false
}

function setDebugPanelVisible(visible: boolean): void {
  localStorage.setItem(DEBUG_PANEL_VISIBILITY_KEY, String(visible));
}

export function toggleDebugPanelVisibility(): boolean {
  const current = isDebugPanelVisible();
  const newValue = !current;
  setDebugPanelVisible(newValue);
  return newValue;
}

/**
 * Default settings: All logging disabled
 */
const DEFAULT_SETTINGS: DebugSettings = {
  enabled: false,
  levels: {
    log: false,
    warn: false,
    error: false,
  },
};

/**
 * Load debug settings from localStorage
 */
function loadSettings(): DebugSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_SETTINGS;

    const parsed = JSON.parse(stored) as DebugSettings;

    // Validate structure
    if (
      typeof parsed.enabled === "boolean" &&
      typeof parsed.levels === "object" &&
      typeof parsed.levels.log === "boolean" &&
      typeof parsed.levels.warn === "boolean" &&
      typeof parsed.levels.error === "boolean"
    ) {
      return parsed;
    }

    return DEFAULT_SETTINGS;
  } catch (error) {
    debug.error("[Debug System] Failed to load settings:", error);
    return DEFAULT_SETTINGS;
  }
}

/**
 * Save debug settings to localStorage
 */
export function saveDebugSettings(settings: DebugSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    // Reload settings
    currentSettings = loadSettings();
  } catch (error) {
    debug.error("[Debug System] Failed to save settings:", error);
  }
}

/**
 * Get current debug settings
 */
export function getDebugSettings(): DebugSettings {
  return { ...currentSettings }; // Return copy to prevent mutation
}

/**
 * Reset debug settings to default (all disabled)
 */
export function resetDebugSettings(): void {
  saveDebugSettings(DEFAULT_SETTINGS);
}

/**
 * Enable debug logging (Admin only)
 */
export function enableDebug(levels?: Partial<DebugSettings["levels"]>): void {
  const newSettings: DebugSettings = {
    enabled: true,
    levels: {
      log: levels?.log ?? true,
      warn: levels?.warn ?? true,
      error: levels?.error ?? true,
    },
  };
  saveDebugSettings(newSettings);
}

/**
 * Disable all debug logging
 */
export function disableDebug(): void {
  saveDebugSettings(DEFAULT_SETTINGS);
}

// Load current settings
let currentSettings = loadSettings();

/**
 * Debug logger with level control
 */
export const debug = {
  /**
   * Log informational message
   */
  log: (...args: any[]): void => {
    if (currentSettings.enabled && currentSettings.levels.log) {
      console.log(...args);
    }
  },

  /**
   * Log warning message
   */
  warn: (...args: any[]): void => {
    if (currentSettings.enabled && currentSettings.levels.warn) {
      console.warn(...args);
    }
  },

  /**
   * Log error message
   * Note: Errors are always logged to console.error for production debugging,
   * but can be suppressed via settings
   */
  error: (...args: any[]): void => {
    if (currentSettings.enabled && currentSettings.levels.error) {
      console.error(...args);
    }
  },

  /**
   * Check if debug is enabled
   */
  isEnabled: (): boolean => currentSettings.enabled,

  /**
   * Check if specific level is enabled
   */
  isLevelEnabled: (level: "log" | "warn" | "error"): boolean => {
    return currentSettings.enabled && currentSettings.levels[level];
  },

  /**
   * Log with performance timing
   */
  time: (label: string): void => {
    if (currentSettings.enabled && currentSettings.levels.log) {
      console.time(label);
    }
  },

  /**
   * End performance timing
   */
  timeEnd: (label: string): void => {
    if (currentSettings.enabled && currentSettings.levels.log) {
      console.timeEnd(label);
    }
  },

  /**
   * Group logs
   */
  group: (label: string): void => {
    if (currentSettings.enabled && currentSettings.levels.log) {
      console.group(label);
    }
  },

  /**
   * End group
   */
  groupEnd: (): void => {
    if (currentSettings.enabled && currentSettings.levels.log) {
      console.groupEnd();
    }
  },

  /**
   * Log table
   */
  table: (data: any): void => {
    if (currentSettings.enabled && currentSettings.levels.log) {
      console.table(data);
    }
  },

  /**
   * Log Debug
   */
  debug: (data: any): void => {
    if (currentSettings.enabled && currentSettings.levels.log) {
      console.debug(data);
    }
  },
};
